// PoLineEntry — the row that puts ONE line on a purchase order.
//
// Two surfaces ask for a PO line and they must ask for it the same way: the
// create form (/procurement/new) building a draft, and the PO detail page
// adding a line to an order that already exists. Everything about the act is
// identical — the three kinds, the fields each kind has, what counts as valid,
// where focus goes afterwards — so it is one component, and the differences
// (which document the line lands on, what surrounds the row) belong to the
// caller.
//
// The component owns the row and nothing else. It hands back a validated
// StagedLine, clears itself and refocuses; what the caller does with that line
// is the caller's business.
//
// ── THREE KINDS OF LINE, ONE ENTRY ROW ───────────────────────────────────────
// A PO line is `stock`, `service` or `charge` (poApi.ts). Stock needs an item, a
// quantity and a unit cost. The other two are a description and one amount, and
// the server defaults their quantity to 1, so this row sends 1 rather than
// asking anybody to invent a count. The segmented control at the top swaps the
// row's fields instead of opening a second form, because it is the same act
// either way: put a line on this order.
//
// ── AND A FOURTH KIND THAT IS NOT A LINE ─────────────────────────────────────
// `discount` sits in the same picker because that is where a buyer looks for
// it (Mannie, 2026-10-12: "beneath the line items, you just add type
// discount"). It is NOT a purchase_order_lines row: the order carries ONE
// discount on its header (migration 110), drawn under the lines, and this row
// hands it to the caller through `onStageDiscount` rather than `onStage`. A
// percent or a dollar amount, with an optional reason the vendor reads on the
// printed order. Callers that cannot take one leave the prop off and the kind
// is not offered.
//
// Service and charge lines never reach receiving (the inbox and Flerpie's queue
// both filter to `line_type = 'stock'`), which is why they carry no item: a
// Receive button on an X-ray inspection fee only ever mints a junk SKU.
//
// ── ITEMS ARE PICKED HERE, NEVER COMPOSED ────────────────────────────────────
// The endpoint takes catalog ids, not SKU strings, so a stock line needs a
// catalog row before it can be staged. There is exactly one way to get one: the
// shared ItemPicker, whose create row opens Parts/NewItemModal, the same New
// Item wizard the catalog page opens. The minted row lands straight back in the
// field that asked for it and the buyer carries on typing quantity.
//
// The line's revision comes off the picked row (parts_catalog.revision). The
// box beside the picker is the buyer's override, for an order placed against an
// iteration the catalog has not recorded yet.
//
// ── THE ROW IS ITS OWN <form> ────────────────────────────────────────────────
// Enter inside it stages a line, which is what makes rapid entry work, and a
// <form> is how the browser already does that. The page around it is therefore
// NOT a form: two nested ones cannot both own Enter. The page's own shortcut
// (Ctrl/Cmd+Enter) passes straight through — see handleRowKeyDown for why that
// needs saying out loud.

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useToast } from '../components/Toast/ToastContext';
import { type PoLineType } from './poApi';
import { type PartsCatalogRow } from '../Receiving/types';
import { SegTrack } from '../components/ui/SegTrack';
import { Appear } from '../components/ui/Appear';
import { buttonClass } from '../components/ui/Button';
import { modalLabel } from '../components/review/fieldStyles';
import { Field, fieldClass } from '../components/ui/Field';
// ONE item picker, everywhere an item is looked up. It searches names and part
// numbers as well as SKUs, and its last row offers to create what nobody found.
import ItemPicker from '../components/ItemPicker';
// The ONE mint form. Opened from the picker's create row; see the file header.
import NewItemModal from '../Parts/NewItemModal';

/** A line staged on a purchase order, in whichever of the three shapes it is.
 *
 *  A stock line's `partCatalogId` is already resolved: nothing leaves this row
 *  until the catalog row behind it exists. A service or charge line has no item
 *  at all, and `lineDescription` is what it is for. */
export interface StagedLine {
  key: string;
  lineType: PoLineType;
  partCatalogId: number | null;
  sku: string | null;
  /** The item's NAME, copied off the row the buyer picked, so the staged table
   *  reads the way every other item surface does. Present on a row the wizard
   *  just minted too: the wizard asks for the name first and answers with the
   *  whole catalog row. Optional only for the reorder handoff, which carries an
   *  id and a SKU and nothing else. */
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  description?: string | null;
  /** Service and charge only: what this line is for, and what the vendor reads
   *  on the printed order. */
  lineDescription: string | null;
  revision: string | null;
  quantity: number;
  unitCostCents: number | null;
  notes: string | null;
}

/** The order's one discount, as the entry row stages it. `pct` set means the
 *  buyer typed a percent and the cents follow the lines (resolved server-side);
 *  `pct` null means `cents` was typed in dollars and stands. */
export interface StagedDiscount {
  pct: number | null;
  cents: number;
  reason: string | null;
}

export interface PoLineEntryHandle {
  /** Put the cursor where the next line starts: the item picker on a stock row,
   *  the description on the other two. */
  focus(): void;
  /** Empty the row without staging anything. The caller needs this when the
   *  document the row was feeding is finished, so a half-typed line does not
   *  follow the buyer onto the next one. */
  clear(): void;
  /** Is anything typed or picked in the row that has NOT been staged? The
   *  detail page asks before Save, because leaving edit mode unmounts the row
   *  and a half-typed line would vanish without a word. */
  hasContent(): boolean;
}

export interface PoLineEntryProps {
  /** Called with a validated line. The component clears its row and refocuses
   *  itself afterwards. */
  onStage: (line: StagedLine) => void;
  /** Asked BEFORE validation. Return false to refuse; the caller does its own
   *  toast and focus (the create form uses this for "Pick the vendor first"). */
  canStage?: () => boolean;
  disabled?: boolean;
  /** The reorder handoff. Applied whenever the object identity changes: sets the
   *  picked item, and the quantity when given. The caller owns where the prefill
   *  came from and any later upgrade of it; it expresses both by handing over a
   *  new object. */
  prefill?: { item: PartsCatalogRow; quantity?: string } | null;
  /** Prefix for the row's input ids, so two rows on one page cannot collide. */
  idPrefix?: string;
  /** Takes the order's discount. Absent, the Discount kind is not offered. */
  onStageDiscount?: (discount: StagedDiscount) => void;
}

/** What the picker offers: the three line kinds, plus the discount. */
export type PoEntryKind = PoLineType | 'discount';

// ── The entry row's shape ────────────────────────────────────────────────────
//
// One horizontal row on md and up, stacking on a phone. Both strings are
// written out whole rather than assembled, because Tailwind reads the source
// for literal class names and would find nothing in a template.
const ENTRY_GRID_STOCK =
  'grid gap-2 items-end md:grid-cols-[minmax(0,2fr)_6rem_5rem_7rem_minmax(0,1.5fr)_auto]';
// A service or charge row has no note field: a PO line has ONE free-text
// column, and on these lines the description IS it.
const ENTRY_GRID_TEXT =
  'grid gap-2 items-end md:grid-cols-[minmax(0,2fr)_7rem_auto]';
// The discount: how it is stated, the figure, why, and the button.
const ENTRY_GRID_DISCOUNT =
  'grid gap-2 items-end md:grid-cols-[auto_7rem_minmax(0,2fr)_auto]';


const LINE_KINDS: { key: PoEntryKind; label: string }[] = [
  { key: 'stock', label: 'Item' },
  { key: 'service', label: 'Service' },
  { key: 'charge', label: 'Charge' },
];
const DISCOUNT_KIND: { key: PoEntryKind; label: string } = { key: 'discount', label: 'Discount' };

/** Dollars or percent: the two ways a vendor states a discount. */
const DISCOUNT_UNITS = [
  { key: 'cents', label: '$' },
  { key: 'pct', label: '%' },
];

const PoLineEntry = React.forwardRef<PoLineEntryHandle, PoLineEntryProps>(
  ({ onStage, onStageDiscount, canStage, disabled = false, prefill = null, idPrefix = 'po' }, ref) => {
    const toast = useToast();

    /** Which shape the entry row is in. Switching clears the row and nothing
     *  else: lines already staged stay exactly where they are. */
    const [lineKind, setLineKind] = useState<PoEntryKind>('stock');
    /** Discount only: whether the figure box holds dollars or a percent. */
    const [discountUnit, setDiscountUnit] = useState<'cents' | 'pct'>('cents');
    const [discountValue, setDiscountValue] = useState('');
    const [discountReason, setDiscountReason] = useState('');
    const [revisionFreeForm, setRevisionFreeForm] = useState('');
    const [quantity, setQuantity] = useState('');
    // Stock reads this as a unit cost, service and charge as the whole line. They
    // are the same number to the server: a non-stock line carries quantity 1, so
    // unit cost times quantity IS the amount.
    const [unitCostUsd, setUnitCostUsd] = useState('');
    const [lineDescription, setLineDescription] = useState('');
    const [lineNotes, setLineNotes] = useState('');

    // --- The item, through the shared picker ---
    // The whole row, not just id + sku: the staged line copies its name off this.
    const [selectedPart, setSelectedPart] = useState<PartsCatalogRow | null>(null);
    /** The New Item wizard, or null when it is shut. The string is whatever the
     *  buyer had typed into the picker before concluding nothing in the catalog
     *  was it, which the wizard takes as the new item's name. Empty when the row
     *  was reached with an empty box. */
    const [wizardSeed, setWizardSeed] = useState<string | null>(null);

    // The whole entry row: its FIRST input is where the next line starts, which
    // is the item picker on a stock row and the description on the other two.
    const entryRef = useRef<HTMLDivElement>(null);
    // Where a row lands once its item exists, which is the only thing still to
    // answer after the wizard closes.
    const quantityRef = useRef<HTMLInputElement>(null);
    // A staged line's key only has to be unique in the list it joins. This is
    // what the caller's `lines.length` used to be, and it never counts down, so
    // removing a line cannot free a key for reuse.
    const seqRef = useRef(0);

    // Put the cursor back where the next line starts, after each added line and
    // whenever the page hands focus over. Rapid entry depends on this: without
    // it you add a line and then reach for the mouse.
    //
    // The wrapper div is held rather than the input, because ItemPicker takes no
    // ref.
    const focusEntry = useCallback(() => {
      entryRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    }, []);

    /** Everything that changes row to row, and nothing that does not. */
    const clearEntryRow = useCallback(() => {
      setSelectedPart(null);
      setRevisionFreeForm('');
      setQuantity('');
      setUnitCostUsd('');
      setLineDescription('');
      setLineNotes('');
      setDiscountValue('');
      setDiscountReason('');
    }, []);

    const hasContent = useCallback(
      () => !!(
        selectedPart
        || quantity.trim()
        || unitCostUsd.trim()
        || lineDescription.trim()
        || lineNotes.trim()
        || revisionFreeForm.trim()
        || discountValue.trim()
        || discountReason.trim()
      ),
      [selectedPart, quantity, unitCostUsd, lineDescription, lineNotes, revisionFreeForm, discountValue, discountReason]
    );

    useImperativeHandle(ref, () => ({ focus: focusEntry, clear: clearEntryRow, hasContent }), [
      focusEntry,
      clearEntryRow,
      hasContent,
    ]);

    // A new prefill object means "this is the item now". Applied when the row is
    // empty, and when it already holds the SAME item — a prefill that arrives
    // late (the caller upgrading a synthesized row to the real catalog one) must
    // never overwrite a different item the buyer has since picked.
    useEffect(() => {
      if (!prefill) return;
      const { item, quantity: qty } = prefill;
      setSelectedPart((cur) => (cur === null || cur.id === item.id ? item : cur));
      if (qty !== undefined) setQuantity(qty);
    }, [prefill]);

    // "Nothing in the catalog is this" — the picker's own create row, opening the
    // one New Item wizard rather than a second mint form built into this row.
    // The typed words become the item's name, which is what the wizard asks for
    // first; it works the rest out with the buyer.
    const openWizard = (query: string) => setWizardSeed(query.trim());

    // The wizard answers with the whole catalog row, so the picker can hold it
    // exactly as if it had been found by searching. Straight to quantity from
    // here: the item is settled, and how many is the only thing left on this row.
    const handleMinted = (row: PartsCatalogRow) => {
      setSelectedPart(row);
      setWizardSeed(null);
      setTimeout(() => quantityRef.current?.focus(), 0);
    };

    // Changing the kind changes which fields the row HAS, so the old row's values
    // have nowhere to go and are cleared. The timeout is not decoration: the new
    // fields do not exist in the DOM until React has re-rendered, and focusEntry
    // reads the DOM.
    const handleKindChange = (key: string) => {
      setLineKind(key as PoEntryKind);
      clearEntryRow();
      setTimeout(() => focusEntry(), 0);
    };

    // Ctrl/Cmd+Enter belongs to the page around this row ("create the order"),
    // not to the row. A browser's implicit form submission ignores modifiers, so
    // that keystroke would stage a line on its way past. Marking it here and
    // dropping the submit that follows is what keeps the two apart; calling
    // preventDefault instead would tell the page's own handler that this
    // keystroke was already spoken for, and the shortcut would do nothing at all.
    const modifiedEnterRef = useRef(false);
    const handleRowKeyDown = (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey)) return;
      modifiedEnterRef.current = true;
      // The submit, if the browser raises one, is dispatched off this very
      // keystroke. Anything later is a different act and must stage normally.
      setTimeout(() => {
        modifiedEnterRef.current = false;
      }, 0);
    };

    // Enter anywhere in the row stages a line.
    const handleAddLine = (e: React.FormEvent) => {
      e.preventDefault();
      if (disabled) return;
      if (modifiedEnterRef.current) {
        modifiedEnterRef.current = false;
        return;
      }

      if (canStage && !canStage()) return;

      const key = (prefix: string | number) => {
        seqRef.current += 1;
        return `${prefix}-${seqRef.current}-${Date.now()}`;
      };

      // ── Discount: a figure, how it is stated, and why ──
      if (lineKind === 'discount') {
        if (!onStageDiscount) return;
        const raw = discountValue.trim();
        const value = raw ? parseFloat(raw) : NaN;
        if (!Number.isFinite(value) || value <= 0) {
          toast.error(discountUnit === 'pct' ? 'Enter the percent off.' : 'Enter the amount off.');
          focusEntry();
          return;
        }
        if (discountUnit === 'pct') {
          const pct = Math.round(value * 100) / 100;
          if (pct > 100) {
            toast.error('A discount cannot be more than 100%.');
            return;
          }
          onStageDiscount({ pct, cents: 0, reason: discountReason.trim() || null });
        } else {
          onStageDiscount({ pct: null, cents: Math.round(value * 100), reason: discountReason.trim() || null });
        }
        clearEntryRow();
        focusEntry();
        return;
      }

      // ── Service and charge: a description and one amount, nothing else ──
      if (lineKind === 'service' || lineKind === 'charge') {
        const text = lineDescription.trim();
        if (!text) {
          toast.error('Give the line a description.');
          focusEntry();
          return;
        }
        const amountCents = unitCostUsd.trim() ? Math.round(parseFloat(unitCostUsd) * 100) : null;
        if (amountCents === null || !Number.isFinite(amountCents) || amountCents < 0) {
          toast.error('Enter the amount for this line.');
          return;
        }
        const kind = lineKind;
        onStage({
          key: key(kind),
          lineType: kind,
          partCatalogId: null,
          sku: null,
          lineDescription: text,
          revision: null,
          // The server would default this to 1; sending it says so out loud, so
          // a reader of the payload never has to know the default to read it.
          quantity: 1,
          unitCostCents: amountCents,
          notes: null,
        });
        clearEntryRow();
        focusEntry();
        return;
      }

      // ── Stock: an item, a count, and what one costs ──
      if (!selectedPart) {
        toast.error('Pick an item from the list first. Nothing there? The list offers to create it.');
        focusEntry();
        return;
      }
      const qty = parseInt(quantity, 10);
      if (!Number.isFinite(qty) || qty <= 0) {
        toast.error('Quantity must be a positive whole number');
        return;
      }
      const unitCostCents = unitCostUsd.trim() ? Math.round(parseFloat(unitCostUsd) * 100) : null;
      if (unitCostCents !== null && (!Number.isFinite(unitCostCents) || unitCostCents < 0)) {
        toast.error('Unit cost must be a non-negative number');
        return;
      }

      // The item's OWN revision, as the catalog records it. The box beside the
      // picker overrides it, for an order placed against an iteration nobody has
      // cataloged yet.
      const pickedRow = selectedPart;
      const revision = revisionFreeForm.trim() || pickedRow.revision || null;

      onStage({
        key: key(pickedRow.id),
        lineType: 'stock',
        partCatalogId: pickedRow.id,
        sku: pickedRow.sku,
        base_name: pickedRow.base_name ?? null,
        display_name: pickedRow.display_name ?? null,
        variant_detail: pickedRow.variant_detail ?? null,
        description: pickedRow.description ?? null,
        lineDescription: null,
        revision,
        quantity: qty,
        unitCostCents,
        notes: lineNotes.trim() || null,
      });

      clearEntryRow();
      focusEntry();
    };

    const isStock = lineKind === 'stock';
    const isDiscount = lineKind === 'discount';
    const entryGrid = isStock ? ENTRY_GRID_STOCK : isDiscount ? ENTRY_GRID_DISCOUNT : ENTRY_GRID_TEXT;

    return (
      <>
        <form className="space-y-4" onSubmit={handleAddLine} onKeyDown={handleRowKeyDown}>
          <SegTrack
            label="What kind of line"
            value={lineKind}
            onChange={handleKindChange}
            options={onStageDiscount ? [...LINE_KINDS, DISCOUNT_KIND] : LINE_KINDS}
          />
          {/* Service and Charge share one entry row, so switching between
              them changes no field a screen reader could notice: same id,
              same label, focus already there. This line is the notice. */}
          <p className="sr-only" role="status">
            {isStock ? 'Adding item lines.' : isDiscount ? 'Adding the order discount.' : `Adding ${lineKind} lines.`}
          </p>

          {/* ONE ROW, whichever kind it is: everything a line needs, left
              to right, ending in the button that stages it. The two column
              templates differ only in how many fields sit between the first
              and the note.

              The item field is the shared picker. It searches names, SKUs,
              part numbers and descriptions, and it owns Enter while its
              list is open: the Enter that takes a row is preventDefault-ed,
              so the submit handler sees defaultPrevented and does not stage
              a line off the same keypress. The second Enter, list now shut,
              stages it. The last row of that list is "New item" and it opens
              the wizard, which is the only way to make one. */}
          <div ref={entryRef} className={entryGrid}>
            {isDiscount ? (
              <>
                {/* The unit first, so the figure box's label can say which it
                    is. A percent follows the lines while the order is being
                    written; dollars stand as typed. */}
                <SegTrack
                  label="Discount as"
                  value={discountUnit}
                  onChange={(key) => setDiscountUnit(key as 'cents' | 'pct')}
                  options={DISCOUNT_UNITS}
                />
                <div>
                  <label className={modalLabel} htmlFor={`${idPrefix}-discount`}>
                    {discountUnit === 'pct' ? 'Percent off' : 'Amount off ($)'}
                  </label>
                  <Field
                    id={`${idPrefix}-discount`}
                    type="number"
                    step="0.01"
                    min={0}
                    max={discountUnit === 'pct' ? 100 : undefined}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountUnit === 'pct' ? '10' : '250.00'}
                  />
                </div>
                <div className="min-w-0">
                  <label className={modalLabel} htmlFor={`${idPrefix}-discount-reason`}>Reason</label>
                  <Field
                    id={`${idPrefix}-discount-reason`}
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    maxLength={200}
                    placeholder="Volume pricing, promo code"
                  />
                </div>
              </>
            ) : isStock ? (
              <>
                <div className="min-w-0">
                  <ItemPicker
                    id={`${idPrefix}-sku-search`}
                    label="Item"
                    value={selectedPart}
                    onSelect={(row) => setSelectedPart(row)}
                    onCreate={openWizard}
                    placeholder="Name, SKU or part number"
                    inputClassName={fieldClass()}
                  />
                </div>
                <div>
                  <label className={modalLabel} htmlFor={`${idPrefix}-rev-existing`}>Revision</label>
                  <Field
                    id={`${idPrefix}-rev-existing`}
                    value={revisionFreeForm}
                    onChange={(e) => setRevisionFreeForm(e.target.value)}
                    placeholder="2.3"
                  />
                </div>
                <div>
                  <label className={modalLabel} htmlFor={`${idPrefix}-qty`}>Qty</label>
                  <Field
                    id={`${idPrefix}-qty`}
                    ref={quantityRef}
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="50"
                  />
                </div>
                <div>
                  <label className={modalLabel} htmlFor={`${idPrefix}-unit-cost`}>Unit cost ($)</label>
                  <Field
                    id={`${idPrefix}-unit-cost`}
                    type="number"
                    step="0.01"
                    min={0}
                    value={unitCostUsd}
                    onChange={(e) => setUnitCostUsd(e.target.value)}
                    placeholder="42.50"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="min-w-0">
                  <label className={modalLabel} htmlFor={`${idPrefix}-line-description`}>Description</label>
                  <Field
                    id={`${idPrefix}-line-description`}
                    value={lineDescription}
                    onChange={(e) => setLineDescription(e.target.value)}
                    placeholder={lineKind === 'service' ? 'X-ray inspection' : 'Freight, DAP'}
                  />
                </div>
                <div>
                  <label className={modalLabel} htmlFor={`${idPrefix}-amount`}>Amount ($)</label>
                  <Field
                    id={`${idPrefix}-amount`}
                    type="number"
                    step="0.01"
                    min={0}
                    value={unitCostUsd}
                    onChange={(e) => setUnitCostUsd(e.target.value)}
                    placeholder="250.00"
                  />
                </div>
              </>
            )}

            <Appear as="div" show={isStock} className="min-w-0">
              <label className={modalLabel} htmlFor={`${idPrefix}-line-notes`}>Line note</label>
              <Field
                id={`${idPrefix}-line-notes`}
                value={lineNotes}
                onChange={(e) => setLineNotes(e.target.value)}
                placeholder="Context for this item"
              />
            </Appear>

            <button type="submit" disabled={disabled} className={buttonClass('quiet')}>
              {isDiscount ? 'Add discount' : 'Add line'}
            </button>
          </div>

          <p className="text-xs text-on-surface-variant">
            {isDiscount
              ? 'One discount per order. Adding another replaces it.'
              : 'Enter adds the line and keeps you typing.'}
          </p>
        </form>

        {/* OUTSIDE the row's form on purpose. The wizard has submit-shaped
            controls of its own, and nested inside that <form> its Create button
            would stage a purchase order line as well as mint an item. */}
        {wizardSeed !== null && (
          <NewItemModal
            initialName={wizardSeed || undefined}
            onMinted={handleMinted}
            onClose={() => {
              setWizardSeed(null);
              focusEntry();
            }}
          />
        )}
      </>
    );
  }
);

PoLineEntry.displayName = 'PoLineEntry';

export default PoLineEntry;
